import React from 'react';
import { AlertTriangle, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

// ===== Global Error Boundary =====
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-8" data-testid="error-boundary">
          <div className="text-center max-w-md space-y-4">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
            <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              The application encountered an unexpected error. Please try again.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={this.handleRetry} variant="outline" data-testid="error-retry-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Try Again
              </Button>
              <Button onClick={() => window.location.href = '/login'} data-testid="error-home-btn">
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ===== Network Status Banner =====
export function NetworkStatusBanner() {
  const [online, setOnline] = React.useState(navigator.onLine);
  const [showReconnected, setShowReconnected] = React.useState(false);

  React.useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      setShowReconnected(true);
      setTimeout(() => setShowReconnected(false), 3000);
    };
    const onOffline = () => { setOnline(false); setShowReconnected(false); };

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (!online) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white text-center py-2 text-sm flex items-center justify-center gap-2" data-testid="network-offline-banner">
        <WifiOff className="h-4 w-4" />
        <span>No internet connection — retrying automatically...</span>
      </div>
    );
  }

  if (showReconnected) {
    return (
      <div className="fixed top-0 left-0 right-0 z-[9999] bg-green-600 text-white text-center py-2 text-sm flex items-center justify-center gap-2" data-testid="network-reconnected-banner">
        <Wifi className="h-4 w-4" />
        <span>Connection restored</span>
      </div>
    );
  }

  return null;
}
